import type { StatusTone } from "../components/ui/StatusBadge";

/**
 * Canjes realizados en el BO (GET /bo/points/redemptions). Tipos camelCase del DTO del backend.
 *
 * En la base el canje es pending, fulfilled o cancelled. "Vencido" no se guarda: es un pending
 * con el QR vencido (`isExpired`). El filtro y la tabla usan los cuatro estados que ve Negocio,
 * que son los mismos cubos disjuntos del Dashboard de Puntos.
 */
export type PointRedemptionStatus = "pending" | "fulfilled" | "cancelled";

export type PointRedemptionStatusFilter = "pending" | "expired" | "fulfilled" | "cancelled";

export type PointRedemptionCancellationReason = "expired" | "user" | "admin";

export interface PointRedemption {
  id: number;
  tenantId: string;
  user: { id: number; fullName: string; email: string };
  reward: { id: number; title: string; partnerName: string | null };
  pointsSpent: number;
  status: PointRedemptionStatus;
  cancellationReason: PointRedemptionCancellationReason | null;
  qrShortCode: string;
  /** Fecha de canje (ISO). */
  createdAt: string;
  /** Fecha de escaneo en el mostrador; null si todavía no se escaneó. */
  fulfilledAt: string | null;
  cancelledAt: string | null;
  qrExpiresAt: string;
  isExpired: boolean;
}

export interface PointRedemptionPage {
  items: PointRedemption[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PointRedemptionFilters {
  /** null = todas las instituciones (SUPER_ADMIN en "Todos"). */
  tenantId: string | null;
  status: PointRedemptionStatusFilter | null;
  /** YYYY-MM-DD, inclusive. */
  from: string | null;
  to: string | null;
  rewardId: number | null;
}

export interface PointRedemptionQuery extends PointRedemptionFilters {
  page: number;
  limit: number;
}

export const POINT_REDEMPTION_STATUS_LABELS: Record<PointRedemptionStatusFilter, string> = {
  pending: "Pendiente",
  expired: "Vencido",
  fulfilled: "Entregado",
  cancelled: "Cancelado",
};

export const POINT_REDEMPTION_STATUS_TONES: Record<PointRedemptionStatusFilter, StatusTone> = {
  pending: "pending",
  expired: "warning",
  fulfilled: "success",
  cancelled: "error",
};

export const POINT_REDEMPTION_CANCELLATION_LABELS: Record<PointRedemptionCancellationReason, string> = {
  expired: "Venció sin usarse",
  user: "Lo canceló el usuario",
  admin: "Lo canceló el BO",
};

/** El estado que ve Negocio: un pendiente con el QR vencido es "Vencido". */
export const resolvePointRedemptionStatus = (
  redemption: Pick<PointRedemption, "status" | "isExpired">
): PointRedemptionStatusFilter =>
  redemption.status === "pending" && redemption.isExpired ? "expired" : redemption.status;

/**
 * Tiempo que le queda a un QR pendiente, contado desde `qrExpiresAt` y no desde la foto que
 * manda el backend (`seconds_remaining`), que queda vieja apenas la tabla lleva un rato abierta.
 * null si ya venció.
 */
export const formatPointRedemptionTimeLeft = (qrExpiresAt: string, nowMs: number): string | null => {
  const expiresMs = Date.parse(qrExpiresAt);
  if (Number.isNaN(expiresMs) || expiresMs <= nowMs) return null;
  const totalMinutes = Math.max(1, Math.floor((expiresMs - nowMs) / 60_000));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `Vence en ${days} d ${hours} h`;
  if (hours > 0) return `Vence en ${hours} h ${minutes} min`;
  return `Vence en ${minutes} min`;
};
